const envApiBase = process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL || '';
const resolveAdminBase = () => {
  const base =
    envApiBase ||
    (typeof window !== 'undefined' ? window.location.origin || '' : '');
  return base ? `${base.replace(/\/$/, '')}/api/admin/index.php` : '/api/admin/index.php';
};
let currentAdminBase =
  process.env.REACT_APP_ADMIN_API || resolveAdminBase();

const resolveUserEndpoint = (path) => {
  const normalizedPath =
    currentAdminBase.includes('/api/admin/index.php') ? path : path.replace(/^\/api\b/i, '');
  // Derive origin from ADMIN_BASE when possible (so localhost:8080 is used in dev)
  try {
    const base = new URL(
      currentAdminBase,
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost'
    );
    return `${base.origin}${normalizedPath}`;
  } catch {
    // Fallback: relative replace
    return currentAdminBase.replace(/\/admin\/index\.php.*$/i, normalizedPath);
  }
};

class ApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.payload = payload;
  }
}

async function request(route, { method = 'GET', data, token } = {}) {
  const url = `${currentAdminBase}?route=${encodeURIComponent(route)}`;
  const headers = {
    'Content-Type': 'application/json',
    'X-Admin-Route': route,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers['X-Auth-Token'] = token; // fallback for hosts that strip Authorization
  }

  let res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  if (res.status === 404 && currentAdminBase.includes('/api/admin/index.php')) {
    currentAdminBase = currentAdminBase.replace('/api/admin/index.php', '/admin/index.php');
    res = await fetch(`${currentAdminBase}?route=${encodeURIComponent(route)}`, {
      method,
      headers,
      body: data ? JSON.stringify(data) : undefined,
      credentials: 'include',
    });
  }

  if (res.status === 404 && typeof window !== 'undefined') {
    const origin = window.location.origin || '';
    const fallback = `${origin}/api/admin/index.php`;
    if (fallback !== currentAdminBase) {
      currentAdminBase = fallback;
      res = await fetch(`${currentAdminBase}?route=${encodeURIComponent(route)}`, {
        method,
        headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include',
      });
    }
  }

  const contentType = res.headers.get('content-type') || '';
  const payload =
    contentType.includes('application/json') ? await res.json() : null;

  if (!res.ok) {
    const message =
      (payload && (payload.error || payload.message)) ||
      `Admin API error (${res.status})`;
    throw new ApiError(message, res.status, payload || {});
  }

  return payload;
}

export const adminApi = {
  login(email, password, fingerprint) {
    return request('login', {
      method: 'POST',
      data: { email, password, fingerprint },
    });
  },

  getSession(token) {
    return request('me', { method: 'GET', token });
  },

  logout(token) {
    return request('logout', { method: 'POST', token });
  },

  createUser(data, token) {
    return request('users', { method: 'POST', data, token });
  },

  listUsers(token) {
    return request('users', { method: 'GET', token });
  },

  listSessions(token) {
    return request('sessions', { method: 'GET', token });
  },

  getUsage(token) {
    return request('usage', { method: 'GET', token });
  },

  logSearch(data, token) {
    return request('search-log', { method: 'POST', data, token });
  },

  logActivity(data, token) {
    return request('activity-log', { method: 'POST', data, token });
  },

  getActivityStats(token) {
    return request('activity-stats', { method: 'GET', token });
  },

  async loadPrefs(token) {
    const base = resolveUserEndpoint('/api/email_workspace.php');
    const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'X-Auth-Token': token || '',
      },
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(`Prefs load failed (${res.status})`, res.status);
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new ApiError(`Prefs load failed: non-JSON response (${res.status}). Body: ${txt.slice(0, 200)}`, res.status);
    }
    return res.json();
  },

  async savePrefs(prefs, token) {
    const base = resolveUserEndpoint('/api/email_workspace.php');
    const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        'X-Auth-Token': token || '',
      },
      body: JSON.stringify({ prefs }),
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(`Prefs save failed (${res.status})`, res.status);
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new ApiError(`Prefs save failed: non-JSON response (${res.status}). Body: ${txt.slice(0, 200)}`, res.status);
    }
    return res.json();
  },

  async loadAccounts(token) {
    const base = resolveUserEndpoint('/api/user_accounts.php');
    const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
        'X-Auth-Token': token || '',
      },
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(`Accounts load failed (${res.status})`, res.status);
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new ApiError(`Accounts load failed: non-JSON response (${res.status}). Body: ${txt.slice(0, 200)}`, res.status);
    }
    return res.json();
  },

  async saveAccounts(accounts, token) {
    const base = resolveUserEndpoint('/api/user_accounts.php');
    const url = token ? `${base}?token=${encodeURIComponent(token)}` : base;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : '',
        'X-Auth-Token': token || '',
      },
      body: JSON.stringify({ accounts }),
      credentials: 'include',
    });
    if (!res.ok) {
      throw new ApiError(`Accounts save failed (${res.status})`, res.status);
    }
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      const txt = await res.text();
      throw new ApiError(`Accounts save failed: non-JSON response (${res.status}). Body: ${txt.slice(0, 200)}`, res.status);
    }
    return res.json();
  },
};

export { ApiError };
